import { z } from 'zod';

import { aiStatusSchema } from '../ai';
import { aiPromptCreateSchema, aiPromptSchema } from '../aiprompt';
import { brandSchema } from '../brand';
import { contentSchema } from '../content';
import { productSchema } from '../product';
import { productSectionSchema } from '../product-section';
import { defaultNullish } from '../shared';

// List
export const listAiPromptReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type ListAiPromptReq = z.infer<typeof listAiPromptReqSchema>;

export const listAiPromptResSchema = {
  200: z.object({
    data: z.array(aiPromptSchema),
  }),
};
export type ListAiPromptRes = z.infer<(typeof listAiPromptResSchema)[200]>;

// Create
export const createAiPromptReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: aiPromptCreateSchema.strip(),
});
export type CreateAiPromptReq = z.infer<typeof createAiPromptReqSchema>;

export const createAiPromptResSchema = {
  200: z.object({
    data: aiPromptSchema,
  }),
};
export type CreateAiPromptRes = z.infer<(typeof createAiPromptResSchema)[200]>;

// Save
export const saveAiPromptReqSchema = z.object({
  params: z.object({
    id: aiPromptSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: aiPromptCreateSchema.partial().strip(),
});
export type SaveAiPromptReq = z.infer<typeof saveAiPromptReqSchema>;

export const saveAiPromptResSchema = {
  200: z.object({
    data: aiPromptSchema,
  }),
};
export type SaveAiPromptRes = z.infer<(typeof saveAiPromptResSchema)[200]>;

// Delete
export const deleteAiPromptReqSchema = z.object({
  params: z.object({
    id: aiPromptSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type DeleteAiPromptReq = z.infer<typeof deleteAiPromptReqSchema>;

export const deleteAiPromptResSchema = {
  200: z.object({}),
};
export type DeleteAiPromptRes = z.infer<(typeof deleteAiPromptResSchema)[200]>;

const aiPromptCommonProps = {
  instruction: z.string(),
  brandId: brandSchema.shape._id.nullish(),
  refineMode: defaultNullish(z.boolean().default(false)),
  previousInstruction: defaultNullish(z.string().nullish().default('')),
  previousResponse: defaultNullish(z.string().nullish().default('')),
  // array of stringified tiptap json of the text to edit
  contentChunk: z.array(z.string()),
  // array of stringified tiptap json of all the text before the text to edit
  contentBefore: z.array(z.string()),
  // array of stringified tiptap json of all the text after the text to edit
  contentAfter: z.array(z.string()),
};

//////////////////////////
// AI assistant prompt
export const assistantPromptReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.discriminatedUnion('type', [
    z
      .object({
        type: z.literal('article'),
        articleId: contentSchema.shape._id,
        ...aiPromptCommonProps,
      })
      .strip(),
    z
      .object({
        type: z.literal('product'),
        productId: productSchema.shape._id,
        productSectionId: productSectionSchema.shape._id,
        ...aiPromptCommonProps,
      })
      .strip(),
  ]),
});
export type AssistantPromptReq = z.infer<typeof assistantPromptReqSchema>;

export const assistantPromptResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type AssistantPromptRes = z.infer<(typeof assistantPromptResSchema)[200]>;

export const contentPromptUpdateSocketResSchema = z.object({
  contentId: z.string(),
  result: z.string(),
  status: aiStatusSchema,
  error: z.string(),
});
export type ContentPromptUpdateSocketResSchema = z.infer<typeof contentPromptUpdateSocketResSchema>;
